import React, { useState } from "react";
import { useAtom } from "jotai";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Box,
    Grid,
    Typography,
    Button,
    TextField,
    Select,
    Collapse,
    MenuItem,
    FormControl,
    InputLabel,
    Switch,
    FormControlLabel,
    InputAdornment,
    Tooltip,
    Checkbox,
    Divider,
    ListItemIcon
} from "@mui/material";
import CurrencyFormat from "react-currency-format";
import { useForm, Controller } from "react-hook-form";
import LoadingButton from "@mui/lab/LoadingButton";
import {
    mbxUserAtom,
    allActiveLocationsAtom,
    allCategoriesAtom,
    allServicesAtom,
    deleteServiceInAllServicesAtom,
    conformationDialogOpenAtom,
    conformationDialogContentAtom,
    confirmationFunctionAtom,
    conformationDialogTitleAtom,
    conformationDialogSubmitButtonAtom,
    conformationDialogIsSubmittingAtom
} from "../atoms/atoms";
import { selectedServiceAtom } from "../atoms/servicesTable";
import { userPageSnackbarAtom } from "../atoms/usersTable";
import { postApi, putApi, delApiWithStatus } from "../modules/AmplifyServices";
import { ThemeProvider, useTheme } from "@mui/material/styles";
import { DialogTheme } from "../styles/DialogTheme";
import { ExpandLess, ExpandMore } from "@mui/icons-material";
import AddIcon from "@mui/icons-material/Add";
import AddCategoriesOnTheFlyDialog from "./AddCategoriesOnTheFlyDialog";
import InfoTooltip from "../utils/UI/InfoTooltip";

export const AddEditServiceDialog = ({ open, onClose }) => {
    const theme = useTheme();
    const [mbxUser] = useAtom(mbxUserAtom);
    const [allActiveLocations] = useAtom(allActiveLocationsAtom);
    const [allCategories] = useAtom(allCategoriesAtom);
    const [allServices, setAllServices] = useAtom(allServicesAtom);
    const [selectedService] = useAtom(selectedServiceAtom);
    const [, deleteServiceInAllServices] = useAtom(
        deleteServiceInAllServicesAtom
    );

    const [, setConfirmationDialogContent] = useAtom(
        conformationDialogContentAtom
    );
    const [, setConfirmationFunction] = useAtom(confirmationFunctionAtom);
    const [, setConfirmationDialogOpen] = useAtom(conformationDialogOpenAtom);
    const [, setConformationDialogTitle] = useAtom(conformationDialogTitleAtom);
    const [, setConformationDialogSubmitButton] = useAtom(
        conformationDialogSubmitButtonAtom
    );
    const [, setConformationDialogIsSubmitting] = useAtom(
        conformationDialogIsSubmittingAtom
    );

    //Snackbar
    const [, setUserPageSnackbar] = useAtom(userPageSnackbarAtom);

    // Add category on the fly dialog state
    const [addCategoryDialogOpen, setAddCategoryDialogOpen] = useState(false);
    const [isDescriptionFocused, setIsDescriptionFocused] = useState(false);
    const [openTaxSettings, setOpenTaxSettings] = useState(false);

    const [isSaving, setIsSaving] = useState(false);
    const handleOpenAddCategoryDialog = () => setAddCategoryDialogOpen(true);
    const handleCloseAddCategoryDialog = () => setAddCategoryDialogOpen(false);

    const TAX_OPTIONS = [
        {
            label: "Automatic per region",
            value: "taxRate"
        },
        {
            label: "Apply custom tax percentage",
            value: "custom"
        },
        {
            label: "This service is tax exempt",
            value: "taxexempt"
        }
    ];

    const TAX_TOOLTIP =
        "In United States, Canada and Australia, the tax % is calculated automatically based on which state / province the client is located in";

    const parseTaxOverride = (val) => {
        if (val === null || val === undefined) return "";
        try {
            const parsed = JSON.parse(val);
            if (typeof parsed === "number" || typeof parsed === "string") {
                return String(parsed);
            }
        } catch (e) {}
        return String(val);
    };

    const mapServiceToFormValues = (service) => {
        const taxSetting = service?.taxexempt
            ? "taxexempt"
            : service?.TaxOverride && service.TaxOverride !== ""
            ? "custom"
            : "taxRate";
        return {
            active: service?.active ?? true,
            serviceType: service?.Classification ?? "SIMPLE",
            serviceName: service?.name ?? "",
            categoryName: service?.categoryName ?? "",
            locations: service?.locations
                ? [...JSON.parse(service.locations)]
                : [],
            durationType: "Fixed",
            duration: service?.minutes ? String(service.minutes) : "60",
            allowRecurring: service?.allowRecurring ?? false,
            TaxOverride: parseTaxOverride(service?.TaxOverride),
            taxexempt: service?.taxexempt ?? false,
            bookingBehaviour: service?.behavior ?? "EXTERNAL",
            price: service?.price ?? "",
            description: service?.desc ?? "",
            taxSetting
        };
    };
    const isEditing = Boolean(selectedService && selectedService.id);
    const {
        control,
        handleSubmit,
        watch,
        reset,
        setValue,
        setError,
        clearErrors,
        formState: { error, isValid, dirtyFields }
    } = useForm({
        mode: "onChange",
        defaultValues: mapServiceToFormValues(selectedService)
    });

    const watchTaxSetting = watch("taxSetting");

    const validateServiceName = (serviceName) => {
        const trimmedName = serviceName.trim().toUpperCase();
        const editingId = selectedService?.id;
        return allServices.find(
            (s) =>
                s.name.trim().toUpperCase() === trimmedName &&
                s.id !== editingId
        );
    };

    const handleDeleteService = async () => {
        setConformationDialogIsSubmitting(true);
        try {
            const { statusCode = null, message = "" } = await delApiWithStatus(
                "serviceManagement",
                `/service/${selectedService.id}`,
                {
                    queryParams: {
                        companyId: mbxUser.companyId,
                        serviceId: selectedService.id
                    }
                }
            );

            if (statusCode >= 200 && statusCode < 300) {
                setUserPageSnackbar({
                    open: true,
                    message: "Service has been successfully deleted",
                    severity: "success"
                });
                deleteServiceInAllServices(selectedService.id);
                setConformationDialogIsSubmitting(false);
                setConfirmationDialogOpen(false);
                onClose();
            } else {
                setUserPageSnackbar({
                    open: true,
                    message: `Unexpected error while deleteing service ${statusCode}`,
                    severity: "error"
                });
                setConformationDialogIsSubmitting(false);
                setConfirmationDialogOpen(false);
                throw new Error(`Error deleting service. Error: ${message}`);
            }
        } catch (error) {
            console.log("Error deleting service:", error);
            setConformationDialogIsSubmitting(false);
            setConfirmationDialogOpen(false);
        }
    };

    const onSubmit = async (data) => {
        clearErrors("serviceName");
        const duplicate = validateServiceName(data.serviceName);

        if (duplicate) {
            setError("serviceName", {
                type: "manual",
                message: "Service name already exists."
            });
            return;
        }

        setIsSaving(true);

        // Map form fields to API payload keys
        const fieldMap = {
            active: "active",
            serviceType: "Classification",
            serviceName: "name",
            categoryName: "categoryName",
            locations: "locations",
            durationType: "durationType",
            duration: "minutes",
            allowRecurring: "allowRecurring",
            TaxOverride: "TaxOverride",
            taxexempt: "taxexempt",
            bookingBehaviour: "behavior",
            price: "price",
            description: "desc"
        };

        const taxSetting = data.taxSetting;
        if (taxSetting === "taxRate") {
            data.TaxOverride = null;
            data.taxexempt = false;
        } else if (taxSetting === "custom") {
            data.taxexempt = false;
        } else if (taxSetting === "taxexempt") {
            data.TaxOverride = null;
            data.taxexempt = true;
        }

        const selectedCategory = allCategories.find(
            (cat) => cat.name === data.categoryName
        );
        let servicePayload = {
            companyId: mbxUser.companyId,
            name: data.serviceName,
            categoryName: data.categoryName,
            categoryId: selectedCategory.id,
            TaxOverride: data.TaxOverride,
            taxexempt: data.taxexempt
        };

        Object.keys(dirtyFields).forEach((formKey) => {
            if (formKey === "categoryName") {
                const selectedCategory = allCategories.find(
                    (cat) => cat.name === data.categoryName
                );
                if (selectedCategory) {
                    servicePayload["categoryId"] = selectedCategory.id;
                    servicePayload["categoryName"] = selectedCategory.name;
                }
            } else if (formKey === "price") {
                servicePayload["price"] = parseFloat(
                    data.price.replace(/,/g, "")
                );
            } else if (fieldMap[formKey]) {
                servicePayload[fieldMap[formKey]] = data[formKey];
            }
        });

        if (!isEditing) {
            // Always send all relevant fields on create
            const selectedCategory = allCategories.find(
                (cat) => cat.name === data.categoryName
            );
            servicePayload = {
                companyId: mbxUser.companyId,
                name: data.serviceName,
                desc: data.description,
                minutes: data.duration,
                behavior: data.bookingBehaviour,
                price: data.price,
                taxexempt: data.taxexempt,
                allowRecurring: data.allowRecurring,
                Classification: data.serviceType,
                locations: data.locations,
                categoryName: data.categoryName,
                categoryId: selectedCategory.id,
                category: selectedCategory
            };
        }
        try {
            let response;
            if (isEditing) {
                response = await putApi(
                    "serviceManagement",
                    `/service/${selectedService.id}`,
                    { body: servicePayload }
                );
            } else {
                response = await postApi("serviceManagement", "/service", {
                    body: servicePayload
                });
            }
            if (response?.service && response?.service?.id) {
                setUserPageSnackbar({
                    open: true,
                    message: isEditing
                        ? "Service has been successfully updated"
                        : "Service has been successfully created",
                    severity: "success"
                });
                if (isEditing) {
                    // update service in allServices
                    setAllServices(
                        allServices.map((service) =>
                            service.id === response.service.id
                                ? response.service
                                : service
                        )
                    );
                } else {
                    setAllServices([response.service, ...allServices]);
                }
                handleClose();
                return response;
            }
        } catch (e) {
            console.log(e);
            handleClose();
        }
        setIsSaving(false);
    };

    const handleClose = () => {
        reset(mapServiceToFormValues(selectedService));
        clearErrors("serviceName");
        onClose();
    };

    return (
        <ThemeProvider theme={DialogTheme(theme)}>
            <Dialog
                maxWidth={"md"}
                open={open}
                onClose={onClose}
                aria-labelledby="-edit-service-dialog-title"
            >
                <DialogTitle
                    id="add-edit-service-dialog-title"
                    sx={{ paddingBottom: "18px !important" }}
                >
                    {selectedService
                        ? `${selectedService.name}`
                        : "Add service"}
                </DialogTitle>
                <form
                    key={selectedService?.id || "newService"}
                    onSubmit={handleSubmit(onSubmit)}
                    style={{
                        overflowY: "auto",
                        display: "flex",
                        flexDirection: "column",
                        height: "100%"
                    }}
                >
                    <DialogContent>
                        <Grid container item spacing={2}>
                            <Grid item xs={12}>
                                <Controller
                                    name="active"
                                    control={control}
                                    render={({ field }) => (
                                        <FormControlLabel
                                            control={
                                                <Switch
                                                    {...field}
                                                    checked={field.value}
                                                />
                                            }
                                            label="Active"
                                        />
                                    )}
                                />
                            </Grid>
                        </Grid>

                        <Grid container item spacing={2}>
                            <Grid item xs={12} sm={6}>
                                <FormControl fullWidth disabled={true}>
                                    <InputLabel size="small">
                                        Service type
                                    </InputLabel>
                                    <Controller
                                        name="serviceType"
                                        control={control}
                                        render={({
                                            field: {
                                                onChange: onChangeFormHook,
                                                value
                                            }
                                        }) => (
                                            <Select
                                                id="serviceType"
                                                name="serviceType"
                                                labelId="serviceType-label"
                                                label="ServiceType"
                                                sx={{
                                                    "& fieldset": {
                                                        "& legend": {
                                                            width: "65px"
                                                        }
                                                    }
                                                }}
                                                value={value}
                                                onChange={(e) => {
                                                    onChangeFormHook(
                                                        e.target.value
                                                    );
                                                }}
                                            >
                                                <MenuItem value={"SIMPLE"}>
                                                    Standard
                                                </MenuItem>

                                                <MenuItem value={"GROUP"}>
                                                    Course
                                                </MenuItem>
                                            </Select>
                                        )}
                                    />
                                </FormControl>
                            </Grid>
                        </Grid>

                        <Grid container item spacing={2}>
                            <Grid item xs={12} sm={12}>
                                <Controller
                                    name="serviceName"
                                    control={control}
                                    rules={{
                                        required: "Please enter a service name",
                                        validate: (value) => {
                                            if (!value.trim())
                                                return "Please enter a service name";
                                            const duplicate =
                                                validateServiceName(value);
                                            if (duplicate)
                                                return `Service with the name "${value}" already exists. Please use another name`;
                                            return true;
                                        }
                                    }}
                                    render={({ field, fieldState }) => (
                                        <TextField
                                            {...field}
                                            label="Service name"
                                            size="small"
                                            fullWidth
                                            error={!!fieldState.error}
                                            helperText={
                                                fieldState.error?.message || ""
                                            }
                                        />
                                    )}
                                />
                            </Grid>
                        </Grid>

                        <Grid container item spacing={2}>
                            <Grid item xs={12} sm={6}>
                                <FormControl fullWidth>
                                    <InputLabel
                                        id="category-label"
                                        size="small"
                                    >
                                        Category
                                    </InputLabel>
                                    <Controller
                                        name="categoryName"
                                        control={control}
                                        rules={{
                                            required: "Please select a category"
                                        }}
                                        render={({
                                            field: {
                                                onChange: onChangeFormHook,
                                                value: categoryName
                                            }
                                        }) => (
                                            <>
                                                <Select
                                                    labelId="category-label"
                                                    id="categoryName"
                                                    name="categoryName"
                                                    label="category"
                                                    value={categoryName}
                                                    sx={{
                                                        "& fieldset": {
                                                            "& legend": {
                                                                width: "55px"
                                                            }
                                                        }
                                                    }}
                                                    onChange={(e) => {
                                                        onChangeFormHook(
                                                            e.target.value
                                                        );
                                                    }}
                                                >
                                                    {allCategories.map(
                                                        (category) => (
                                                            <MenuItem
                                                                value={
                                                                    category.name
                                                                }
                                                                key={
                                                                    category.name
                                                                }
                                                            >
                                                                <Tooltip
                                                                    title={
                                                                        category
                                                                            .name
                                                                            .length >
                                                                        80
                                                                            ? category.name
                                                                            : ""
                                                                    }
                                                                    placement="right"
                                                                >
                                                                    <Typography
                                                                        noWrap
                                                                        sx={{
                                                                            overflow:
                                                                                "hidden",
                                                                            textOverflow:
                                                                                "ellipsis",
                                                                            whiteSpace:
                                                                                "nowrap",
                                                                            width: "100%",
                                                                            cursor: "pointer"
                                                                        }}
                                                                    >
                                                                        {category
                                                                            .name
                                                                            .length >
                                                                        80
                                                                            ? `${category.name.substring(
                                                                                  0,
                                                                                  80
                                                                              )}...`
                                                                            : category.name}
                                                                    </Typography>
                                                                </Tooltip>
                                                            </MenuItem>
                                                        )
                                                    )}
                                                    <Grid
                                                        item
                                                        variant={
                                                            "dropdownDivider"
                                                        }
                                                    >
                                                        <Divider />
                                                    </Grid>
                                                    <MenuItem
                                                        onClick={() =>
                                                            handleOpenAddCategoryDialog()
                                                        }
                                                        sx={{
                                                            fontWeight: "bold",
                                                            color: theme.palette
                                                                .primary.main,
                                                            "& .MuiListItemIcon-root":
                                                                {
                                                                    minWidth:
                                                                        "32px"
                                                                }
                                                        }}
                                                        disableRipple
                                                        disableGutters
                                                    >
                                                        <ListItemIcon>
                                                            <AddIcon
                                                                fontSize="small"
                                                                sx={{
                                                                    color: theme
                                                                        .palette
                                                                        .primary
                                                                        .main
                                                                }}
                                                            />
                                                        </ListItemIcon>
                                                        <Typography
                                                            sx={{
                                                                fontWeight:
                                                                    "bold",
                                                                color: theme
                                                                    .palette
                                                                    .primary
                                                                    .main
                                                            }}
                                                        >
                                                            Add new category
                                                        </Typography>
                                                    </MenuItem>
                                                </Select>
                                                <AddCategoriesOnTheFlyDialog
                                                    open={addCategoryDialogOpen}
                                                    onClose={
                                                        handleCloseAddCategoryDialog
                                                    }
                                                    onCategoryCreated={(
                                                        cat
                                                    ) => {
                                                        // Set the select value to the new category's name
                                                        setValue(
                                                            "categoryName",
                                                            cat.name,
                                                            {
                                                                shouldValidate: true
                                                            }
                                                        );
                                                    }}
                                                />
                                            </>
                                        )}
                                    />
                                </FormControl>
                            </Grid>

                            <Grid item xs={12} sm={6}>
                                <FormControl fullWidth>
                                    <InputLabel size="small">
                                        Booking type
                                    </InputLabel>
                                    <Controller
                                        name="bookingBehaviour"
                                        control={control}
                                        render={({
                                            field: {
                                                onChange: onChangeFormHook,
                                                value
                                            }
                                        }) => (
                                            <Select
                                                id="bookingBehaviour"
                                                name="bookingBehaviour"
                                                labelId="booking-behaviour"
                                                label="Booking type"
                                                sx={{
                                                    "& fieldset": {
                                                        "& legend": {
                                                            width: "72px"
                                                        }
                                                    },
                                                    ".MuiSelect-select": {
                                                        paddingRight:
                                                            "20px !important"
                                                    }
                                                }}
                                                value={value}
                                                onChange={(e) => {
                                                    onChangeFormHook(
                                                        e.target.value
                                                    );
                                                }}
                                            >
                                                <MenuItem value={"EXTERNAL"}>
                                                    Bookable by clients and
                                                    admins
                                                </MenuItem>
                                                <MenuItem value={"INTERNAL"}>
                                                    Bookable by admins only
                                                </MenuItem>
                                                <MenuItem value={"MANUAL"}>
                                                    Waitlist
                                                </MenuItem>
                                            </Select>
                                        )}
                                    />
                                </FormControl>
                            </Grid>
                        </Grid>

                        <Grid container item spacing={2}>
                            <Grid item xs={12} sm={12}>
                                <FormControl fullWidth sx={{ gap: 0.5 }}>
                                    <InputLabel
                                        id="locations-label"
                                        size="small"
                                    >
                                        Locations
                                    </InputLabel>
                                    <Controller
                                        name="locations"
                                        control={control}
                                        render={({
                                            field: {
                                                onChange: onChangeFormHook,
                                                value: locations
                                            }
                                        }) => (
                                            <Select
                                                labelId="locations-label"
                                                id="locations"
                                                name="locations"
                                                label="Locations"
                                                multiple
                                                value={locations}
                                                sx={{
                                                    "& fieldset": {
                                                        "& legend": {
                                                            width: "55px"
                                                        }
                                                    }
                                                }}
                                                onChange={(e) => {
                                                    onChangeFormHook(
                                                        e.target.value
                                                    );
                                                }}
                                                renderValue={() =>
                                                    locations.length ===
                                                    allActiveLocations.length
                                                        ? "All locations"
                                                        : allActiveLocations
                                                              .filter(
                                                                  (location) =>
                                                                      locations.includes(
                                                                          location.id
                                                                      )
                                                              )
                                                              .map(
                                                                  (location) =>
                                                                      location.locationname
                                                              )
                                                              .join(", ")
                                                }
                                            >
                                                {allActiveLocations.map(
                                                    (location) => (
                                                        <MenuItem
                                                            value={location.id}
                                                        >
                                                            <Checkbox
                                                                checked={locations.includes(
                                                                    location.id
                                                                )}
                                                            />
                                                            {
                                                                location.locationname
                                                            }
                                                        </MenuItem>
                                                    )
                                                )}
                                            </Select>
                                        )}
                                    />
                                </FormControl>
                            </Grid>
                        </Grid>

                        <Grid container item spacing={2}>
                            <Grid item xs={6} sm={3}>
                                <FormControl fullWidth disabled={true}>
                                    <InputLabel size="small">
                                        Duration type
                                    </InputLabel>
                                    <Controller
                                        name="durationType"
                                        control={control}
                                        render={({
                                            field: {
                                                onChange: onChangeFormHook,
                                                value
                                            }
                                        }) => (
                                            <Select
                                                id="durationType"
                                                fullWidth
                                                name="durationType"
                                                labelId="duration-type"
                                                label="Duration type"
                                                sx={{
                                                    "& fieldset": {
                                                        "& legend": {
                                                            width: "70px"
                                                        }
                                                    }
                                                }}
                                                value={value}
                                                onChange={(e) => {
                                                    onChangeFormHook(
                                                        e.target.value
                                                    );
                                                }}
                                            >
                                                <MenuItem value={"Fixed"}>
                                                    Fixed
                                                </MenuItem>
                                                <MenuItem value={"Variable"}>
                                                    Variable
                                                </MenuItem>
                                            </Select>
                                        )}
                                    />
                                </FormControl>
                            </Grid>
                            <Grid item xs={6} sm={3}>
                                <Controller
                                    name="duration"
                                    control={control}
                                    render={({ field }) => (
                                        <TextField
                                            {...field}
                                            label="Duration"
                                            size="small"
                                            InputProps={{
                                                endAdornment: (
                                                    <InputAdornment
                                                        position="end"
                                                        disableTypography
                                                    >
                                                        mins
                                                    </InputAdornment>
                                                )
                                            }}
                                        />
                                    )}
                                />
                            </Grid>
                        </Grid>

                        <Grid container item spacing={2}>
                            <Grid
                                item
                                xs={12}
                                sm={6}
                                sx={{
                                    display: "flex",
                                    height: "50px",
                                    alignItems: "center"
                                }}
                            >
                                <Controller
                                    name="allowRecurring"
                                    control={control}
                                    render={({ field }) => (
                                        <FormControlLabel
                                            control={
                                                <Switch
                                                    {...field}
                                                    checked={field.value}
                                                />
                                            }
                                            label="Allow recurring appointments"
                                        />
                                    )}
                                />
                            </Grid>
                        </Grid>

                        <Grid container item spacing={2}>
                            <Grid item xs={6} sm={3}>
                                <Controller
                                    name="price"
                                    control={control}
                                    rules={{
                                        required: "Please enter a price"
                                    }}
                                    render={({ field }) => (
                                        <CurrencyFormat
                                            {...field}
                                            customInput={TextField}
                                            label="Price"
                                            size="small"
                                            fullWidth
                                            thousandSeparator={true}
                                            InputProps={{
                                                startAdornment: (
                                                    <InputAdornment
                                                        position="start"
                                                        disableTypography
                                                    >
                                                        $
                                                    </InputAdornment>
                                                )
                                            }}
                                        />
                                    )}
                                />
                            </Grid>
                        </Grid>

                        <Grid container item spacing={2}>
                            <Grid
                                item
                                xs={12}
                                sx={{
                                    display: "flex",
                                    height: "50px",
                                    alignItems: "center"
                                }}
                            >
                                <Button
                                    name="switch-notes"
                                    disabled={isSaving}
                                    onClick={() =>
                                        setOpenTaxSettings((open) => !open)
                                    }
                                    variant="text"
                                    color="primary"
                                    disableRipple
                                    endIcon={
                                        openTaxSettings ? (
                                            <ExpandLess />
                                        ) : (
                                            <ExpandMore />
                                        )
                                    }
                                    sx={{ textTransform: "none" }}
                                >
                                    Tax settings
                                </Button>
                            </Grid>
                        </Grid>
                        <Collapse in={openTaxSettings}>
                            <Grid container item spacing={2}>
                                <Grid item xs={12} sm={6}>
                                    <FormControl fullWidth>
                                        <InputLabel
                                            id="tax-setting-label"
                                            shrink
                                        >
                                            Tax
                                        </InputLabel>
                                        <Controller
                                            name="taxSetting"
                                            control={control}
                                            defaultValue="taxRate"
                                            render={({ field }) => (
                                                <Select
                                                    {...field}
                                                    labelId="tax-setting-label"
                                                    label="Tax"
                                                    sx={{
                                                        ".MuiSelect-select": {
                                                            paddingRight:
                                                                "10px !important"
                                                        }
                                                    }}
                                                    value={field.value}
                                                >
                                                    {TAX_OPTIONS.map(
                                                        (option) => (
                                                            <MenuItem
                                                                value={
                                                                    option.value
                                                                }
                                                                key={
                                                                    option.value
                                                                }
                                                            >
                                                                {option.label}
                                                            </MenuItem>
                                                        )
                                                    )}
                                                </Select>
                                            )}
                                        />
                                    </FormControl>
                                </Grid>
                                {watchTaxSetting === "custom" && (
                                    <Grid item xs={12} sm={3}>
                                        <Controller
                                            name="TaxOverride"
                                            control={control}
                                            rules={{
                                                required:
                                                    "Enter a custom tax %",
                                                pattern: {
                                                    value: /^[0-9]+(\.[0-9]{1,2})?$/,
                                                    message:
                                                        "Enter a valid number"
                                                },
                                                min: {
                                                    value: 0,
                                                    message: "Must be 0 or more"
                                                },
                                                max: {
                                                    value: 100,
                                                    message:
                                                        "Cannot exceed 100%"
                                                }
                                            }}
                                            render={({ field, fieldState }) => (
                                                <TextField
                                                    {...field}
                                                    label="Custom tax"
                                                    size="small"
                                                    error={!!fieldState.error}
                                                    helperText={
                                                        fieldState.error
                                                            ?.message
                                                    }
                                                    InputProps={{
                                                        endAdornment: (
                                                            <InputAdornment position="end">
                                                                %
                                                            </InputAdornment>
                                                        )
                                                    }}
                                                />
                                            )}
                                        />
                                    </Grid>
                                )}
                                <Box
                                    sx={{
                                        paddingTop: "15px",
                                        paddingLeft: "10px",
                                        display: "flex",
                                        alignItems: "center"
                                    }}
                                >
                                    <InfoTooltip
                                        title={TAX_TOOLTIP}
                                    ></InfoTooltip>
                                </Box>
                            </Grid>
                        </Collapse>
                        <Grid container spacing={2}>
                            <Grid item xs={12}>
                                <Controller
                                    name="description"
                                    control={control}
                                    render={({ field }) => (
                                        <TextField
                                            {...field}
                                            label="Description (public)"
                                            fullWidth
                                            multiline
                                            rows={5}
                                            onFocus={() =>
                                                setIsDescriptionFocused(true)
                                            }
                                            onBlur={() =>
                                                setIsDescriptionFocused(false)
                                            }
                                            InputLabelProps={{
                                                style: {
                                                    top:
                                                        isDescriptionFocused ||
                                                        field.value.length
                                                            ? "2px"
                                                            : "-8px"
                                                }
                                            }}
                                            sx={{
                                                "& .MuiOutlinedInput-root": {
                                                    height: 100,
                                                    minHeight: 100,
                                                    maxHeight: 100,
                                                    overflow: "hidden",
                                                    "& .MuiOutlinedInput-notchedOutline":
                                                        {
                                                            borderTopWidth:
                                                                isDescriptionFocused
                                                                    ? "3px"
                                                                    : "1px"
                                                        }
                                                },
                                                "& .MuiOutlinedInput-input": {
                                                    paddingTop: "20px"
                                                }
                                            }}
                                        />
                                    )}
                                />
                            </Grid>
                        </Grid>
                        <Grid
                            item
                            xs={12}
                            sm={12}
                            sx={{
                                paddingTop: "27px !important"
                            }}
                        >
                            <Button
                                onClick={() => {
                                    setConfirmationFunction(() =>
                                        handleDeleteService()
                                    );
                                    setConfirmationDialogContent(
                                        <Typography>
                                            Are you sure you want to delete this
                                            service?
                                        </Typography>
                                    );
                                    setConformationDialogTitle(
                                        "Delete service"
                                    );
                                    setConformationDialogSubmitButton("Delete");
                                    setConfirmationDialogOpen(true);
                                }}
                                variant="text"
                                disabled={!selectedService}
                                color="error"
                                sx={{
                                    "&:hover": {
                                        backgroundColor:
                                            "transparent !important"
                                    }
                                }}
                                disableRipple
                            >
                                <Typography
                                    variant="button"
                                    sx={{
                                        textTransform: "none",
                                        lineHeight: "14px"
                                    }}
                                >
                                    {`Delete service`}
                                </Typography>
                            </Button>
                        </Grid>
                    </DialogContent>
                    <DialogActions>
                        <Grid
                            container
                            item
                            gap="18px"
                            sx={{
                                display: "flex",
                                justifyContent: "flex-end"
                            }}
                        >
                            <Button
                                onClick={handleClose}
                                variant="cancelButton"
                                disabled={isSaving}
                            >
                                Cancel
                            </Button>
                            <LoadingButton
                                color="primary"
                                variant="contained"
                                type="submit"
                                loading={isSaving}
                                disabled={
                                    !isValid ||
                                    Object.keys(dirtyFields).length === 0
                                }
                            >
                                Save
                            </LoadingButton>
                        </Grid>
                    </DialogActions>
                </form>
            </Dialog>
        </ThemeProvider>
    );
};
